import React, {useState} from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle, Grid,
    TextField,
    Typography
} from '@mui/material';
import {useMutationAsUser, useMutationCoordinate, useQueryAsUser} from '../../clients/graphql/graphql.hooks';
import EditIcon from '@mui/icons-material/Edit';
import SpeedIcon from '@mui/icons-material/Speed';
import { useNavigate } from 'react-router-dom';
import {UserInstitutionsList} from "../institutions/UserInstitutionsList";
import Divider from "@mui/material/Divider";
import {
    Agreement,
  GQL_SubscriptionPaymentInfoGet,
  GQL_UserEdit,
  GQL_WhoAmIWithSubscriptionsAndAgreements,
  GQL_UserInsights,
  WhoAmIBasic,
  WhoAmIWithSubscriptionsAndAgreements,
  UserInsights
} from "../../clients/graphql/views/user-queries";
import {GQL_JediUserEdit} from "../../clients/graphql/pages/users-queries";
import {formatMoment} from "../../functions/momentFormatter";
import {useUserPortfolioLpoaFetch} from "../../clients/graphql/pages/lpoa-queries";
import {LpoaSummaryCard} from "../../components/lpoa/LpoaSummaryCard";

export type UserCrudProps = {
    userKey: string | number
}
export function UserCrudPanel({ userKey }: UserCrudProps) {

    const [editing, setEditing] = useState<boolean>(false);
    const { loading, error, data, refetch } = useQueryAsUser(GQL_WhoAmIWithSubscriptionsAndAgreements, { userKey, variables: { userKey }, errorPolicy: 'all' })


    return <>
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between'}}>
            <Typography variant="subtitle1" component="div" align="left"
                        sx={{
                            color: '#6D6D6D',
                            font: 'Roboto',
                            fontWeight: 500,
                            fontSize: '13px',
                            lineHeight: '12px',
                            letterSpacing: '0.05em',
                            paddingLeft: '.75rem',
                            paddingTop: '.75rem',
                            paddingBottom: '.25rem',
                        }}>
                DETAILS
            </Typography>
            <Button
                color="inherit"
                onClick={() => setEditing(true)}
                sx={{
                    minWidth: 'auto',
                    minHeight: 'auto',
                    height: '10'
                }}>
                <EditIcon fontSize='small'/>
            </Button>
        </Box>
        {
            loading ? <CircularProgress  sx={{ marginLeft: '40%', marginTop: '50px'}}/> :
                !data?.whoami ? <Alert severity="warning">{error?.message || 'Failed to load user data'}</Alert> :
                    <NewUserCrudDisplay userKey={userKey} whoami={data?.whoami} />
        }
        {data &&
            <UserUpdateDialog isOpen={editing} handleClose={() => { setEditing(false); refetch(); }}
                              whoami={data?.whoami} userKey={userKey} />
        }
    </>
}

type NewUserCrudDisplayProps = {
    userKey: string | number,
    whoami: WhoAmIWithSubscriptionsAndAgreements
}

function NewUserCrudDisplay({ userKey, whoami }: NewUserCrudDisplayProps) {
    // Fetch LPOA data for the new summary component
    const { data: lpoaData, loading: lpoaLoading, error: lpoaError } = useUserPortfolioLpoaFetch({
        userKey: parseInt(userKey.toString())
    });

    // Fetch user insights separately to prevent failures from blocking other data
    const { data: insightsData } = useQueryAsUser(GQL_UserInsights, {
        userKey,
        variables: { userKey },
        errorPolicy: 'all'  // Continue even if this query fails
    });
    const userInsights = insightsData?.whoami?.userInsights;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' , paddingBottom: "1rem", borderRadius: '8px'}}>
                <Box sx={{
                    textAlign: 'left',
                    borderRadius: '8px',
                    marginLeft: '.75rem',
                    marginRight: '.75rem',
                    overflowX: 'auto',
                    whiteSpace: 'nowrap',
                    marginBottom: '1rem'
                }}>
                    <Box sx={{display: 'flex', flexDirection: 'row',}}>
                        <Typography sx={{
                            marginRight: '0.25rem',
                        }}>{whoami?.firstName} {whoami?.lastName}:</Typography>
                        <Typography fontWeight={'lighter'}>
                            <>{whoami?.userKey}</>
                        </Typography>
                    </Box>
                    <Typography fontWeight={'lighter'}>{whoami?.email}</Typography>
                    <Typography fontWeight={'lighter'}>{whoami?.phoneNumber?.replace(/(\d{0,1})(\d{3})(\d{3})(\d{4})/, '$1 ($2) $3-$4' ?? '')}</Typography>
                    <Box sx={{display: 'flex', flexDirection: 'row',}}>
                        <Typography sx={{
                            marginRight: '0.25rem'
                        }}>Created at:</Typography>
                        <Typography fontWeight={'lighter'}> {formatMoment(whoami?.createdAt.toLocaleString())}
                        </Typography>
                    </Box>
                </Box>

                <Typography variant="subtitle1" component="div" align="left"
                            sx={{
                                color: '#6D6D6D',
                                font: 'Roboto',
                                fontWeight: 500,
                                fontSize: '13px',
                                lineHeight: '12px',
                                letterSpacing: '0.05em',
                                paddingLeft: '.75rem',
                                paddingTop: '.75rem',
                                paddingBottom: '.25rem',
                            }}>
                    INSIGHTS
                </Typography>
                <InsightsCard userInsights={userInsights} />

                <Typography variant="subtitle1" component="div" align="left"
                            sx={{
                                color: '#6D6D6D',
                                font: 'Roboto',
                                fontWeight: 500,
                                fontSize: '13px',
                                lineHeight: '12px',
                                letterSpacing: '0.05em',
                                paddingLeft: '.75rem',
                                paddingTop: '.75rem',
                                paddingBottom: '.25rem',
                            }}>
                    SUBSCRIPTIONS
                </Typography>
                {SubscriptionCard(userKey)}

                <Typography variant="subtitle1" component="div" align="left"
                            sx={{
                                color: '#6D6D6D',
                                font: 'Roboto',
                                fontWeight: 500,
                                fontSize: '13px',
                                lineHeight: '12px',
                                letterSpacing: '0.05em',
                                paddingLeft: '.75rem',
                                paddingTop: '.75rem',
                                paddingBottom: '.25rem',
                            }}>
                    AGREEMENTS
                </Typography>
                <AgreementsCard agreements={whoami.agreements} />

                <Typography variant="subtitle1" component="div" align="left"
                            sx={{
                                color: '#6D6D6D',
                                font: 'Roboto',
                                fontWeight: 500,
                                fontSize: '13px',
                                lineHeight: '12px',
                                letterSpacing: '0.05em',
                                paddingLeft: '.75rem',
                                paddingTop: '.75rem',
                                paddingBottom: '.25rem',
                            }}>
                    LPOA STATUS
                </Typography>
                <LpoaSummaryCard 
                    userKey={userKey} 
                    lpoaData={lpoaData?.UserPortfolioLpoaFetch || []}
                    loading={lpoaLoading}
                    error={lpoaError}
                />

                <Typography variant="subtitle1" component="div" align="left"
                            sx={{
                                color: '#6D6D6D',
                                font: 'Roboto',
                                fontWeight: 500,
                                fontSize: '13px',
                                lineHeight: '12px',
                                letterSpacing: '0.05em',
                                paddingLeft: '.75rem',
                                paddingTop: '.75rem',
                                paddingBottom: '.25rem',
                            }}>
                    DEVICES
                </Typography>
                {DeviceCard(whoami)}

            <Typography variant="subtitle1" component="div" align="left"
                        sx={{
                            color: '#6D6D6D',
                            font: 'Roboto',
                            fontWeight: 500,
                            fontSize: '13px',
                            lineHeight: '12px',
                            letterSpacing: '0.05em',
                            paddingLeft: '.75rem',
                            paddingTop: '.75rem',
                            paddingBottom: '.25rem',
                        }}>
                INSTITUTIONS
            </Typography>
                <UserInstitutionsList userKey={parseInt(userKey.toString())}/>
        </Box>
    )
}

type UserUpdateDialogProps = {
    userKey: string | number,
    isOpen: boolean,
    handleClose: any,
    whoami: WhoAmIBasic
}
function UserUpdateDialog({ userKey, isOpen, handleClose, whoami }: UserUpdateDialogProps) {

    const [firstName, setFirstName] = useState<String>(whoami?.firstName);
    const [lastName, setLastName] = useState<String>(whoami.lastName);
    const [email, setEmail] = useState<String>(whoami.email);
    const [phoneNumber, setPhoneNumber] = useState<String>(whoami.phoneNumber);
    const [userEditMutation, userEditState] = useMutationAsUser(GQL_UserEdit, {
        userKey: parseInt(userKey.toString()),
        variables: {
            firstName,
            lastName,
            email
        }
    })
    const [jediUserEditMutation, jediUserEditState] = useMutationCoordinate(GQL_JediUserEdit, {
        variables: {
            userKey: parseInt(userKey.toString()), // No idea why but this fixes some casting issues
            phoneNumber
        }
    })

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        try {
            await Promise.all([
                userEditMutation(),
                jediUserEditMutation()
            ]);
            handleClose();
        } catch (e) {
            // eat and render the error
        }
    }

    const loading = userEditState?.loading || jediUserEditState?.loading;

    return <Dialog
        open={isOpen}
        onClose={handleClose}
        PaperProps={{
            component: 'form',
            onSubmit: handleSubmit,
        }}
    >
        <DialogTitle>Edit Customer</DialogTitle>
        {userEditState?.error &&
            <Alert severity="warning">{userEditState?.error?.message}</Alert>
        }
        {jediUserEditState?.error &&
            <Alert severity="warning">{jediUserEditState?.error?.message}</Alert>
        }
        <DialogContent >
            <TextField
                autoFocus
                required
                margin="dense"
                id="firstName"
                name="firstName"
                label="First Name"
                type="text"
                fullWidth
                variant="standard"
                value={firstName}
                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setFirstName(e.target.value)}
            />
            <TextField
                autoFocus
                required
                margin="dense"
                id="lastName"
                name="lastName"
                label="Last Name"
                type="text"
                fullWidth
                variant="standard"
                value={lastName}
                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setLastName(e.target.value)}
            />
            <TextField
                autoFocus
                required
                margin="dense"
                id="phoneNumber"
                name="phoneNumber"
                label="Phone Number"
                type="phone"
                fullWidth
                variant="standard"
                value={phoneNumber}
                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setPhoneNumber(e.target.value)}
            />
            <TextField
                autoFocus
                required
                margin="dense"
                id="name"
                name="email"
                label="Email Address"
                type="email"
                fullWidth
                variant="standard"
                value={email}
                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setEmail(e.target.value)}
            />
        </DialogContent>
        <DialogActions>
            <Button onClick={handleClose} disabled={loading}>Cancel</Button>
            <Button type="submit" variant='contained' disabled={loading}>Update User</Button>
        </DialogActions>
    </Dialog>
}

function SubscriptionCard(userKey: string | number){
    const navigate = useNavigate();
    const numericUserKey = parseInt(userKey.toString());
    const {data, loading, error} = useQueryAsUser(GQL_SubscriptionPaymentInfoGet, { userKey: numericUserKey, variables: { userKey: numericUserKey }, errorPolicy: 'all' })

    const subscriptions = data?.subscriptionPaymentInfoGet?.subscriptions;

    // Check if any subscription has a metered price
    const hasMeteredPrice = subscriptions?.some((subscription: any) =>
        subscription.products?.some((product: any) =>
            product.prices?.some((price: any) => price.usageType?.toUpperCase() === 'METERED')
        )
    );

    if (loading) {
        return (
            <Box sx={{ marginLeft: '.75rem', marginRight: '.75rem', marginBottom: '1rem' }}>
                <CircularProgress size={20} />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ marginLeft: '.75rem', marginRight: '.75rem', marginBottom: '1rem' }}>
                <Alert severity="warning" sx={{ fontSize: '0.8rem' }}>
                    {error.message}
                </Alert>
            </Box>
        );
    }

    if (!subscriptions?.length) {
        return (
            <Box sx={{
                border: (theme) => theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.15)' : '1px solid lightgray',
                borderRadius: '8px',
                padding: '1rem',
                marginLeft: '.75rem',
                marginRight: '.75rem',
                marginBottom: '1rem',
            }}>
                <Typography fontWeight={'lighter'} sx={{ fontStyle: 'italic' }}>
                    No active subscriptions
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{
            border: (theme) => theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.15)' : '1px solid lightgray',
            borderRadius: '8px',
            paddingLeft: '1rem',
            paddingRight: '1rem',
            paddingBottom: '1rem',
            marginLeft: '.75rem',
            marginRight: '.75rem',
            overflowX: 'auto',
            whiteSpace: 'nowrap',
            marginBottom: '1rem',
        }}>
            {subscriptions.map((subscription: any, index: number) => (
                <Box key={index} sx={{ pt: 1 }}>
                    <Typography>
                        {subscription.products[0]?.name}
                    </Typography>
                    <Typography fontWeight={'lighter'}>
                        ${subscription.products[0]?.prices[0]?.unitAmount / 100} {subscription.products[0]?.prices[0]?.displayInterval}
                    </Typography>
                    <Box sx={{display: 'flex', flexDirection: 'row',}}>
                        <Typography sx={{
                            marginRight: '0.25rem',
                            fontSize: 'small'
                        }}>SUBSCRIBED SINCE:</Typography>
                        <Typography fontWeight={'lighter'} fontSize={'small'}>
                            {formatMoment(subscription.createdAt?.toLocaleString())}
                        </Typography>
                    </Box>
                    {index < subscriptions.length - 1 && (
                        <Divider sx={{ my: 1 }} />
                    )}
                </Box>
            ))}
            {hasMeteredPrice && (
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<SpeedIcon />}
                    onClick={() => navigate(`/users/metered-usage/${numericUserKey}`)}
                    sx={{
                        mt: 1.5,
                        textTransform: 'none',
                        fontWeight: 500,
                        borderColor: '#6366f1',
                        color: '#6366f1',
                        '&:hover': {
                            borderColor: '#4f46e5',
                            backgroundColor: 'rgba(99, 102, 241, 0.04)',
                        }
                    }}
                >
                    View Usage
                </Button>
            )}
        </Box>
    );
}

type AgreementsCardProps = {
    agreements: Agreement[];
};

function AgreementsCard({ agreements }: AgreementsCardProps) {  
    return (
      <Box
        sx={{
          border: (theme) => theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.15)' : '1px solid lightgray',
          borderRadius: '8px',
          padding: '1rem',
          marginLeft: '.75rem',
          marginRight: '.75rem',
          overflowX: 'scroll',
          whiteSpace: 'nowrap',
          marginBottom: '1rem',
        }}
      >
        {agreements?.length > 0 &&
            agreements.map((agreement, index) => (
                <div key={index}>
                    <Typography
                        component="a"
                        href={agreement.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ textDecoration: 'underline', color: 'primary.main' }}
                    >
                        {agreement.name}
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                        <Typography
                        sx={{
                            marginRight: '0.25rem',
                            fontSize: 'small',
                        }}
                        >
                        SIGNED ON:
                        </Typography>
                        <Typography fontWeight="lighter" fontSize="small">
                        {formatMoment(agreement?.signedOn?.toLocaleString())}
                        </Typography>
                    </Box>
                    <Divider sx={{ my: 1 }} />
                </div>
            ))}
        </Box>
    );
}

function DeviceCard(whoami: WhoAmIWithSubscriptionsAndAgreements){
    return (
        <Box sx={{
            borderRadius: '8px',
            border: (theme) => theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.15)' : '1px solid lightgray',
            justifyContent: 'space-between',
            textAlign: 'left',
            paddingLeft: '1rem',
            paddingRight: '1rem',
            marginLeft: '.75rem',
            marginRight: '.75rem',
            marginBottom: '1rem',
        }}>
            <Grid container spacing={0}>
                <Grid item xs={6} textAlign="left">
                    <Typography>{whoami?.metadata?.deviceOS}</Typography>
                </Grid>
                <Grid item xs={6} textAlign="right">
                    <Typography fontWeight="light">{whoami?.metadata?.deviceOSVersion}</Typography>
                </Grid>
                <Grid item xs={6} textAlign="left">
                    <Typography>App Version</Typography>
                </Grid>
                <Grid item xs={6} textAlign="right">
                    <Typography fontWeight="light">{whoami?.metadata?.appVersionName}</Typography>
                </Grid>
            </Grid>
        </Box>
    );
}

type InsightsCardProps = {
    userInsights?: UserInsights;
};

function InsightsCard({ userInsights }: InsightsCardProps) {
    if (!userInsights) {
        return null;
    }

    const userHealthPercent = userInsights.userHealth ? userInsights.userHealth : null;

    return (
        <Box sx={{
            border: (theme) => theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.15)' : '1px solid lightgray',
            borderRadius: '8px',
            padding: '1rem',
            marginLeft: '.75rem',
            marginRight: '.75rem',
            marginBottom: '1rem',
        }}>
            {userHealthPercent !== null ? (
                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                    <Typography sx={{ marginRight: '0.25rem' }}>User Health:</Typography>
                    <Typography fontWeight={'lighter'}>
                        {userHealthPercent}%
                    </Typography>
                </Box>
            ) : (
                <Typography fontWeight={'lighter'} sx={{ fontStyle: 'italic' }}>
                    No insights available
                </Typography>
            )}
        </Box>
    );
}
